import {
  Badge,
  Box,
  Button,
  ButtonList,
  DataTable,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  Icon,
  Page,
  SearchInput,
  Skeleton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { sizes, typography } from '@dixa/lib-shared-tokens';
import Fuse from 'fuse.js';
import { ampli } from 'lib-shared-tracking';
import { Role } from 'lib-shared-user-role';
import { useMemo, useState } from 'react';

import { rolesApi } from 'apis/roles';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { isDeleteCustomRoleErrorWithReason } from './settingsAgentsRoles.types';

export const SettingsAgentsRolesView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const [searchTerm, setSearchTerm] = useState('');

  const { roles, isLoading, isError, isSuccess, refetch } =
    rolesApi.useReadRolesQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        roles: data?.data.roles ?? [],
      }),
    });

  const filteredRoles = useMemo(() => {
    const minMatchCharLength = 2;

    if (!searchTerm || searchTerm.length < minMatchCharLength) {
      return roles;
    }

    const fuse = new Fuse(roles, {
      keys: ['id', 'name', 'description'],
      threshold: 0.2,
      minMatchCharLength,
      distance: 200,
    });

    return fuse.search(searchTerm).map(({ item }) => item);
  }, [roles, searchTerm]);

  const [deleteCustomRole] = rolesApi.useDeleteCustomRoleMutation();

  const handleDeleteCustomRole = async (role: Role) => {
    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${role.name}"`,
            description: 'Are you sure you want to delete this custom role?',
            confirm: {
              label: 'Delete',
              async: true,
              asyncLabel: 'Deleting...',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      confirm(async () => {
        try {
          await deleteCustomRole({ roleId: role.id }).unwrap();

          notify(
            {
              title: 'Custom role deleted',
              text: `"${role.name}" has been deleted.`,
              icon: 'i-done',
              time: 3000,
            },
            NotificationType.Success,
          );

          ampli.settingsRolesCustomRoleDeleted();
        } catch (error) {
          notify(
            {
              title: 'Error while deleting the custom role.',
              text: isDeleteCustomRoleErrorWithReason(error)
                ? error.data.title
                : undefined,
            },
            NotificationType.Error,
          );
        }
      });
    }
  };

  return (
    <Page.Section
      title="All roles"
      titleMeta={
        roles.length && (
          <Badge size="small" variant="secondary">
            {roles.length}
          </Badge>
        )
      }
      controls={
        <>
          <SearchInput
            onSubmit={setSearchTerm}
            onToggle={(isOpen) => !isOpen && setSearchTerm('')}
          />

          <AppLink path="/settings/agents/roles/create">
            <Button variant="primary">Create custom role</Button>
          </AppLink>
        </>
      }
    >
      <DataTable
        columns={[
          {
            label: 'Role name',
            selector: (role) => {
              return (
                <FlexBox gap="300" alignItems="center">
                  {isLoading ? (
                    <Skeleton
                      width={sizes.spacer[500]}
                      height={sizes.spacer[500]}
                    />
                  ) : (
                    <Tooltip
                      dark
                      wrap="div"
                      wrapProps={{ style: { display: 'flex' } }}
                      content={
                        role.is_system_role ? 'System role' : 'Custom role'
                      }
                    >
                      <Icon
                        name={
                          role.is_system_role ? 'role-system' : 'role-custom'
                        }
                        color={
                          role.is_system_role
                            ? typography.color.tertiary
                            : typography.color.primary
                        }
                        size={24}
                      />
                    </Tooltip>
                  )}

                  <Box>
                    {isLoading ? (
                      <Skeleton height="14px" width="128px" />
                    ) : (
                      <>
                        <AppLink
                          path="/settings/agents/roles/:roleType/:roleId/permissions"
                          params={{
                            path: {
                              roleType: role.is_system_role
                                ? 'system'
                                : 'custom',
                              roleId: role.id,
                            },
                          }}
                        >
                          <Text.Paragraph
                            variant="1"
                            as="span"
                            subdued={role.is_system_role}
                          >
                            {role.name}
                          </Text.Paragraph>
                        </AppLink>

                        {!role.is_system_role && role.description && (
                          <Text.Paragraph subdued variant="2">
                            {role.description}
                          </Text.Paragraph>
                        )}
                      </>
                    )}
                  </Box>
                </FlexBox>
              );
            },
          },
          {
            label: '',
            selector: (role) => {
              if (isLoading) {
                return (
                  <FlexBox
                    justifyContent="center"
                    alignItems="center"
                    width="32px"
                    height="32px"
                  >
                    <Skeleton height="18px" width="18px" />
                  </FlexBox>
                );
              }

              return (
                <DropdownButton
                  icon="dots-h"
                  buttonProps={{ variant: 'tertiary' }}
                  noArrow
                  fixed
                  dropdown={
                    <DropdownButtonContext.Consumer>
                      {({ toggleDropdown }) => (
                        <ButtonList>
                          {!role.is_system_role && (
                            <AppLink
                              path="/settings/agents/roles/:roleType/:roleId/permissions"
                              params={{
                                path: {
                                  roleType: role.is_system_role
                                    ? 'system'
                                    : 'custom',
                                  roleId: role.id,
                                },
                              }}
                            >
                              <ButtonList.Item>
                                Edit permissions
                              </ButtonList.Item>
                            </AppLink>
                          )}

                          <AppLink
                            path="/settings/agents/roles/:roleType/:roleId/agents"
                            params={{
                              path: {
                                roleType: role.is_system_role
                                  ? 'system'
                                  : 'custom',
                                roleId: role.id,
                              },
                            }}
                          >
                            <ButtonList.Item>Manage agents</ButtonList.Item>
                          </AppLink>

                          {!role.is_system_role && (
                            <>
                              <ButtonList.Divider />
                              <ButtonList.Item
                                onClick={() => {
                                  handleDeleteCustomRole(role);
                                  toggleDropdown();
                                }}
                              >
                                Delete
                              </ButtonList.Item>
                            </>
                          )}
                        </ButtonList>
                      )}
                    </DropdownButtonContext.Consumer>
                  }
                />
              );
            },
            style: {
              width: 0,
            },
          },
        ]}
        data={
          isLoading
            ? (Array.from({ length: 25 }) as typeof roles)
            : filteredRoles
        }
        tableStatusProps={{
          loadingFailed: isError,
          onLoadingRetry: refetch,
          emptyState:
            !filteredRoles.length && isSuccess
              ? {
                  title: 'No roles found',
                  description: 'Try changing your search criteria.',
                }
              : undefined,
        }}
        tableStyleProps={{
          stickyHeader: true,
          stickyFooter: true,
        }}
      />
    </Page.Section>
  );
};
